import React, { useState, useRef } from 'react';
import { FormGroup, Label, Input, Button, Alert, Row, Col } from 'reactstrap';
import { IconCopy, IconCheck, IconDatabase, IconFileCode, IconDownload, IconUpload } from '@tabler/icons-react';
import { useDesignStore } from '../../../store/designStore';
import DbSyncSection from './DbSyncSection';

const ExportConfigTab = ({ config = {} }) => {
    const [copiedQuery, setCopiedQuery] = useState(false);
    const [copiedJson, setCopiedJson] = useState(false);
    const [uploadError, setUploadError] = useState(null);
    const [uploadSuccess, setUploadSuccess] = useState(false);

    const queryRef = useRef(null);
    const jsonRef = useRef(null);
    const fileInputRef = useRef(null);

    const { updateLocalConfig } = useDesignStore();

    const { _id, __v, ...restConfig } = config;

    const mongoUpdateQuery = `db.clientrenders.updateOne(
  { _id: ObjectId("${_id || 'YOUR_DOCUMENT_ID'}") },
  {
    $set: ${JSON.stringify(restConfig, null, 2).split('\n').join('\n    ')}
  }
);`;

    const rawJson = JSON.stringify(restConfig, null, 2);

    const handleCopy = (text, inputElement, setCopied) => {
        let copied = false;

        if (inputElement) {
            try {
                inputElement.select();
                const doc = inputElement.ownerDocument || document;
                copied = doc.execCommand('copy');
            } catch (err) {
                console.warn('execCommand copy failed, trying clipboard API...', err);
            }
        }

        if (!copied && navigator.clipboard) {
            navigator.clipboard
                .writeText(text)
                .then(() => {
                    setCopied(true);
                    setTimeout(() => setCopied(false), 2000);
                })
                .catch((err) => {
                    console.error('Clipboard API copy failed', err);
                });
        } else if (copied) {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);

            try {
                const doc = inputElement.ownerDocument || document;
                const win = doc.defaultView || window;
                if (win.getSelection) {
                    win.getSelection().removeAllRanges();
                }
            } catch (selErr) {
                // Ignore selection clear failure
            }
        }
    };

    const handleDownload = () => {
        try {
            const blob = new Blob([rawJson], { type: 'application/json' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `design-config-${_id || 'config'}.json`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        } catch (err) {
            console.error('Download failed', err);
        }
    };

    const handleUploadClick = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    const handleFileChange = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = (event) => {
            try {
                const parsed = JSON.parse(event.target.result);
                if (typeof parsed !== 'object' || parsed === null) {
                    throw new Error('Datei muss ein JSON-Objekt sein.');
                }
                const { _id: importedId, __v: importedV, ...cleanConfig } = parsed;
                updateLocalConfig(cleanConfig);
                setUploadSuccess(true);
                setUploadError(null);
                setTimeout(() => setUploadSuccess(false), 4000);
            } catch (err) {
                console.error('Failed to parse JSON file:', err);
                setUploadError('Ungültige JSON-Datei oder fehlerhaftes Format.');
                setUploadSuccess(false);
                setTimeout(() => setUploadError(null), 5000);
            }
            e.target.value = '';
        };
        reader.readAsText(file);
    };

    return (
        <div>
            <div className="mb-4">
                <h2 className="h6 text-uppercase text-muted fw-bold border-bottom pb-2 mb-3">
                    Datenbank-Synchronisierung & Export
                </h2>

                {/* Direct Database Sync Component */}
                <DbSyncSection />

                {/* File Import / Export Section */}
                <h3
                    className="h6 text-uppercase text-muted fw-bold border-bottom pb-2 mb-3 mt-4"
                    style={{ fontSize: '0.8rem' }}
                >
                    Datei Import & Export (JSON)
                </h3>
                <p className="text-muted small mb-3">
                    Speichern Sie Ihr Design als lokale JSON-Datei ab oder laden Sie eine zuvor gespeicherte
                    Konfigurationsdatei hoch, um sie live zu betrachten.
                </p>

                {uploadSuccess && (
                    <Alert color="success" className="small py-2 mb-3">
                        ✓ <strong>Erfolg:</strong> Die Konfigurationsdatei wurde erfolgreich geladen und live
                        angewendet!
                    </Alert>
                )}

                {uploadError && (
                    <Alert color="danger" className="small py-2 mb-3">
                        ✗ <strong>Fehler:</strong> {uploadError}
                    </Alert>
                )}

                <Row className="g-2 mb-4">
                    <Col xs={12} sm={6}>
                        <Button
                            color="primary"
                            className="w-100 d-flex align-items-center justify-content-center py-2"
                            onClick={handleDownload}
                        >
                            <IconDownload size={18} className="me-2" />
                            Konfiguration exportieren (.json)
                        </Button>
                    </Col>
                    <Col xs={12} sm={6}>
                        <input
                            type="file"
                            accept=".json"
                            ref={fileInputRef}
                            style={{ display: 'none' }}
                            onChange={handleFileChange}
                        />
                        <Button
                            color="secondary"
                            outline
                            className="w-100 d-flex align-items-center justify-content-center py-2"
                            onClick={handleUploadClick}
                        >
                            <IconUpload size={18} className="me-2" />
                            Konfiguration importieren (.json)
                        </Button>
                    </Col>
                </Row>

                {/* Manual Export Fallbacks */}
                <h3
                    className="h6 text-uppercase text-muted fw-bold border-bottom pb-2 mb-3 mt-4"
                    style={{ fontSize: '0.8rem' }}
                >
                    Manuelle Datenbankabfragen (Fallback)
                </h3>
                <p className="text-muted small mb-3">
                    Exportieren Sie Ihre Live-Design-Konfigurationen, um sie manuell auf Ihr Datenbankdokument im
                    Backend anzuwenden.
                </p>

                <Alert color="info" className="small d-flex align-items-start py-2">
                    <span className="me-2" style={{ fontSize: '1.2rem', lineHeight: '1' }}>
                        ℹ️
                    </span>
                    <div>
                        <strong>Dokument-ID:</strong> Diese Konfiguration entspricht der Dokument-ID{' '}
                        <code>{_id || 'unbekannt'}</code>. Die folgende Abfrage ist bereits mit dieser ID
                        voreingestellt, sodass Sie diese direkt kopieren und ausführen können.
                    </div>
                </Alert>

                <FormGroup className="mb-4">
                    <div className="d-flex justify-content-between align-items-center mb-1">
                        <Label className="fw-bold mb-0 d-flex align-items-center" style={{ fontSize: '0.85rem' }}>
                            <IconDatabase size={16} className="me-1 text-primary" />
                            MongoDB Update-Abfrage
                        </Label>
                        <Button
                            size="sm"
                            color={copiedQuery ? 'success' : 'primary'}
                            outline
                            className="d-flex align-items-center px-2 py-1"
                            style={{ fontSize: '0.75rem' }}
                            onClick={() => handleCopy(mongoUpdateQuery, queryRef.current, setCopiedQuery)}
                        >
                            {copiedQuery ? (
                                <>
                                    <IconCheck size={14} className="me-1" />
                                    Kopiert!
                                </>
                            ) : (
                                <>
                                    <IconCopy size={14} className="me-1" />
                                    Abfrage kopieren
                                </>
                            )}
                        </Button>
                    </div>
                    <Input
                        type="textarea"
                        innerRef={queryRef}
                        value={mongoUpdateQuery}
                        readOnly
                        rows={8}
                        className="font-monospace small bg-white text-dark"
                        style={{ fontSize: '0.8rem', resize: 'vertical' }}
                    />
                </FormGroup>

                <FormGroup className="mb-3">
                    <div className="d-flex justify-content-between align-items-center mb-1">
                        <Label className="fw-bold mb-0 d-flex align-items-center" style={{ fontSize: '0.85rem' }}>
                            <IconFileCode size={16} className="me-1 text-secondary" />
                            Roh-JSON (Ohne Metadaten)
                        </Label>
                        <Button
                            size="sm"
                            color={copiedJson ? 'success' : 'primary'}
                            outline
                            className="d-flex align-items-center px-2 py-1"
                            style={{ fontSize: '0.75rem' }}
                            onClick={() => handleCopy(rawJson, jsonRef.current, setCopiedJson)}
                        >
                            {copiedJson ? (
                                <>
                                    <IconCheck size={14} className="me-1" />
                                    Kopiert!
                                </>
                            ) : (
                                <>
                                    <IconCopy size={14} className="me-1" />
                                    JSON kopieren
                                </>
                            )}
                        </Button>
                    </div>
                    <Input
                        type="textarea"
                        innerRef={jsonRef}
                        value={rawJson}
                        readOnly
                        rows={8}
                        className="font-monospace small bg-white text-dark"
                        style={{ fontSize: '0.8rem', resize: 'vertical' }}
                    />
                </FormGroup>
            </div>
        </div>
    );
};

export default ExportConfigTab;
